/* Copyright (c) 2021-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { useEffect, useState } from "react";
import styled from "styled-components";
import { Button, FlexContainer, theme } from "@nordcloud/gnui";
import { isNotEmpty } from "~/tools";
import { InputName } from "./InputName";
import { InputValue } from "./InputValue";

type Props = {
  inputTags?: string[];
  onChange: (tags: string[]) => void;
  width?: string;
  placeholder?: {
    key: string;
    value: string;
  };
  showIcon?: boolean;
};

enum KEY_VALUE {
  KEY = 0,
  VALUE = 1,
}

const emptyTag = ["", ""];

export function MultiTagsInput({
  inputTags,
  onChange,
  width,
  placeholder,
  showIcon = false,
}: Props) {
  const [tags, setTags] = useState<string[][]>(getTags(inputTags));

  useEffect(() => {
    setTags(getTags(inputTags));
  }, [inputTags, setTags]);

  const onChangeHandler = (value: string, index: number, field: KEY_VALUE) => {
    const newTags = [...tags];
    newTags[index].splice(field, 1, value);
    setTags(newTags);
    onChange(
      newTags
        .filter((tag) => isNotEmpty(tag.join("")))
        .map((tag) => tag.join(","))
    );
  };

  const onRemoveTagHandler = (index: number) => {
    const newTags = [...tags];
    newTags.splice(index, 1);
    setTags(newTags);
    onChange(
      newTags
        .filter((tag) => isNotEmpty(tag.join("")))
        .map((tag) => tag.join(","))
    );
  };

  return (
    <>
      {tags?.map((tag, index) => (
        <FlexContainer
          key={`tag-pair-${index}`}
          alignItems="center"
          columnGap={theme.spacing.spacing02}
          marginBottom={theme.spacing.spacing02}
        >
          <InputWrapper width={width}>
            <InputName
              inputValue={tag[0] ?? ""}
              placeholder={placeholder?.key ?? undefined}
              onChange={(tagName) =>
                onChangeHandler(tagName, index, KEY_VALUE.KEY)
              }
            />
          </InputWrapper>
          <InputWrapper width={width}>
            <InputValue
              name={tag[0] ?? ""}
              inputValue={tag[1] ?? ""}
              placeholder={placeholder?.value ?? undefined}
              onChange={(tagValue) =>
                onChangeHandler(tagValue, index, KEY_VALUE.VALUE)
              }
            />
          </InputWrapper>
          <Button
            severity="low"
            icon="trash"
            onClick={() => onRemoveTagHandler(index)}
          />
        </FlexContainer>
      ))}
      <Button
        severity="low"
        icon={showIcon ? "plus" : undefined}
        onClick={() => setTags([...tags, [...emptyTag]])}
      >
        Add Tag
      </Button>
    </>
  );
}

const InputWrapper = styled.div<{ width?: string }>`
  width: ${({ width }) => width};
`;

function getTags(inputTags: string[] | undefined) {
  return inputTags?.map((tag) => tag.split(",")) ?? [];
}
